import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    doc,
    getDoc,
    setDoc,
    addDoc,
    collection,
    serverTimestamp,
    query,
    where,
    orderBy,
    getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Save, ArrowLeft, CheckCircle, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import SmartCaptureModal from '../components/SmartCaptureModal';
import { apiPost } from '../utils/api'; // 🔹 NEW: unified API helper

// --- Reusable Text Area for Note Sections ---
const NoteSectionTextarea = ({ label, ...props }) => (
    <div>
        <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {label}
        </label>
        <textarea
            {...props}
            className="w-full h-40 bg-slate-100 dark:bg-zinc-800 border-none rounded-lg p-4 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        />
    </div>
);

export default function ProgressNote() {
    const { patientId, noteId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [showSmartCapture, setShowSmartCapture] = useState(false);
    const [noteData, setNoteData] = useState({
        subjective: '',
        intervalHistory: '',
        newSymptoms: '',
        sideEffects: '',
        objective: '',
        priorProblemProgress: '',
        medicationChanges: '',
        ap: '',
        updatedPlan: '',
        type: 'Progress'
    });
    const [patientName, setPatientName] = useState('');
    const [activeTab, setActiveTab] = useState('Subjective');
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [errorMessage, setErrorMessage] = useState(''); // 🔹 NEW: inline error banner

    const noteTabs = ['Subjective', 'Objective', 'A&P', 'Plan'];

    // ─────────────────────────────
    // Fetch Patient Info
    // ─────────────────────────────
    const fetchPatientName = useCallback(async () => {
        if (!patientId) return;
        try {
            const patientRef = doc(db, 'patients', patientId);
            const patientSnap = await getDoc(patientRef);
            if (patientSnap.exists()) {
                const data = patientSnap.data();
                setPatientName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
            }
        } catch (err) {
            console.error('Error fetching patient name:', err);
            setErrorMessage('Unable to load patient info.');
        }
    }, [patientId]);

    useEffect(() => {
        const initializeNote = async () => {
            setErrorMessage('');
            await fetchPatientName();

            try {
                if (noteId && noteId !== 'new') {
                    const noteRef = doc(db, 'notes', noteId);
                    const noteSnap = await getDoc(noteRef);
                    if (noteSnap.exists()) {
                        setNoteData(prev => ({
                            ...prev,
                            ...noteSnap.data()
                        }));
                    }
                } else {
                    // fresh note
                    setNoteData({
                        subjective: '',
                        intervalHistory: '',
                        newSymptoms: '',
                        sideEffects: '',
                        objective: '',
                        priorProblemProgress: '',
                        medicationChanges: '',
                        ap: '',
                        updatedPlan: '',
                        type: 'Progress'
                    });
                }
            } catch (err) {
                console.error('Error initializing note:', err);
                setErrorMessage('Unable to load note data.');
            }
        };

        initializeNote();
    }, [noteId, patientId, fetchPatientName]);

    // ─────────────────────────────
    // Save Note
    // ─────────────────────────────
    const handleSave = async () => {
        if (!currentUser || !patientId) {
            setErrorMessage('Missing user or patient information.');
            return;
        }

        setIsSaving(true);
        setIsSaved(false);
        setErrorMessage('');

        const summary = `Subjective: ${noteData.subjective}\n\nA&P: ${noteData.ap}`;
        const payload = {
            ...noteData,
            summary,
            timestamp: serverTimestamp()
        };

        try {
            if (noteId && noteId !== 'new') {
                const noteRef = doc(db, 'notes', noteId);
                await setDoc(noteRef, payload, { merge: true });
            } else {
                const newNoteRef = await addDoc(collection(db, 'notes'), {
                    ...payload,
                    ownerId: currentUser.uid,
                    patientID: patientId
                });
                // Navigate to the newly created note
                navigate(`/progress-note/${patientId}/${newNoteRef.id}`, { replace: true });
            }
            setIsSaved(true);
        } catch (err) {
            console.error('Error saving note:', err);
            setErrorMessage('Error saving note. Please try again.');
        } finally {
            setIsSaving(false);
            setTimeout(() => setIsSaved(false), 2000);
        }
    };

    // ─────────────────────────────
    // Generate Assessment & Plan (via /v1/generate-ap-v2)
    // ─────────────────────────────
    const handleGenerateAP = async () => {
        if (!patientId) {
            setErrorMessage('Cannot generate A&P without a patient.');
            return;
        }

        setIsGenerating(true);
        setErrorMessage('');

        try {
            // Fetch past notes for this patient (most recent first)
            const pastNotesQuery = query(
                collection(db, 'notes'),
                where('patientID', '==', patientId),
                orderBy('timestamp', 'desc')
            );

            const pastNotesSnapshot = await getDocs(pastNotesQuery);
            const pastNotes = pastNotesSnapshot.docs.map(docSnap => docSnap.data());

            const past_notes_context = pastNotes
                .map(note => {
                    const timestamp =
                        note.timestamp?.toDate?.().toLocaleDateString() || 'N/A';
                    return `--- Past Note (${timestamp}) ---\n${note.summary || ''}`;
                })
                .join('\n\n');

            const current_note_context = `Current Subjective: ${noteData.subjective}\nCurrent Objective: ${noteData.objective}`;

            const apPayload = {
                context: 'progress',
                current_note_context,
                past_notes_context
            };

            // 🔹 NEW: use versioned helper — automatically calls /v1/generate-ap-v2
            const apResult = await apiPost('/generate-ap-v2', apPayload);

            setNoteData(prev => ({
                ...prev,
                ap: apResult.summary || ''
            }));
        } catch (error) {
            console.error('Error generating A&P:', error);
            const msg =
                error?.message ||
                error?.error ||
                'Failed to generate A&P. Please try again.';
            setErrorMessage(msg);
            setNoteData(prev => ({
                ...prev,
                ap: `Error: ${msg}`
            }));
        } finally {
            setIsGenerating(false);
        }
    };

    // ─────────────────────────────
    // Smart Capture (AI Extractor) – /v1/format-progress-text
    // ─────────────────────────────
    const handleSmartCaptureSubmit = async text => {
        if (!text || !text.trim()) return;

        setErrorMessage('');

        try {
            // 🔹 NEW: use helper — hits /v1/format-progress-text
            const result = await apiPost('/format-progress-text', { text });

            setNoteData(prev => ({
                ...prev,
                subjective: result.hpi || prev.subjective,
                objective: result.exam || prev.objective,
                ap: result.assessment_and_plan || prev.ap
            }));
        } catch (error) {
            console.error('Error formatting text:', error);
            const msg =
                error?.message ||
                error?.error ||
                'Smart Capture formatting failed. Please try again.';
            setErrorMessage(msg);
        }
    };

    // ─────────────────────────────
    // Render
    // ─────────────────────────────
    const handleChange = e =>
        setNoteData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    return (
        <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
            <Header />
            <main className="max-w-5xl mx-auto py-10 px-4">
                <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl overflow-hidden">
                    {/* Top Header */}
                    <div className="p-6 border-b border-gray-200 dark:border-zinc-800 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div>
                                <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                                    Progress Note
                                </h1>
                                <p className="text-gray-500 dark:text-gray-400">
                                    for {patientName || 'patient'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowSmartCapture(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                            >
                                <Sparkles size={16} /> Smart Capture
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving || isSaved}
                                className={`flex items-center justify-center gap-2 px-8 py-2 rounded-full font-semibold text-white transition-all duration-300 ${
                                    isSaved
                                        ? 'bg-green-500'
                                        : 'bg-blue-500 hover:bg-blue-600'
                                }`}
                            >
                                {isSaving ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : isSaved ? (
                                    <>
                                        <CheckCircle size={18} /> Saved!
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} /> Save Note
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* 🔹 NEW: subtle inline error banner */}
                    {errorMessage && (
                        <div className="px-6 pt-4">
                            <div className="rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 px-4 py-3 text-sm text-red-700 dark:text-red-200">
                                {errorMessage}
                            </div>
                        </div>
                    )}

                    {/* Tabs + Content */}
                    <div className="p-6">
                        {/* Tabs */}
                        <div className="flex justify-center mb-8">
                            <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
                                {noteTabs.map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className="relative px-5 py-1.5 text-sm font-semibold transition-colors duration-300"
                                    >
                                        <span
                                            className={`relative z-10 ${
                                                activeTab === tab
                                                    ? 'text-gray-800 dark:text-white'
                                                    : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'
                                            }`}
                                        >
                                            {tab}
                                        </span>
                                        {activeTab === tab && (
                                            <motion.div
                                                layoutId="active-note-pill"
                                                className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                                                transition={{
                                                    type: 'spring',
                                                    stiffness: 350,
                                                    damping: 30
                                                }}
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'Subjective' && (
                            <div className="space-y-4">
                                <NoteSectionTextarea
                                    label="Subjective"
                                    name="subjective"
                                    value={noteData.subjective}
                                    onChange={handleChange}
                                />
                                <NoteSectionTextarea
                                    label="Interval History"
                                    name="intervalHistory"
                                    value={noteData.intervalHistory}
                                    onChange={handleChange}
                                />
                                <NoteSectionTextarea
                                    label="New Symptoms"
                                    name="newSymptoms"
                                    value={noteData.newSymptoms}
                                    onChange={handleChange}
                                />
                                <NoteSectionTextarea
                                    label="Side Effects"
                                    name="sideEffects"
                                    value={noteData.sideEffects}
                                    onChange={handleChange}
                                />
                            </div>
                        )}

                        {activeTab === 'Objective' && (
                            <div className="space-y-4">
                                <NoteSectionTextarea
                                    label="Objective"
                                    name="objective"
                                    value={noteData.objective}
                                    onChange={handleChange}
                                />
                                <NoteSectionTextarea
                                    label="Problem Progress"
                                    name="priorProblemProgress"
                                    value={noteData.priorProblemProgress}
                                    onChange={handleChange}
                                />
                                <NoteSectionTextarea
                                    label="Medication Changes"
                                    name="medicationChanges"
                                    value={noteData.medicationChanges}
                                    onChange={handleChange}
                                />
                            </div>
                        )}

                        {activeTab === 'A&P' && (
                            <div className="space-y-4">
                                <NoteSectionTextarea
                                    label="Assessment & Plan"
                                    name="ap"
                                    value={noteData.ap}
                                    onChange={handleChange}
                                />
                                <button
                                    onClick={handleGenerateAP}
                                    disabled={isGenerating}
                                    className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                                >
                                    {isGenerating ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <Sparkles size={16} /> Generate A&P
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {activeTab === 'Plan' && (
                            <NoteSectionTextarea
                                label="Updated Plan"
                                name="updatedPlan"
                                value={noteData.updatedPlan}
                                onChange={handleChange}
                            />
                        )}
                    </div>
                </div>
            </main>

            <SmartCaptureModal
                show={showSmartCapture}
                onClose={() => setShowSmartCapture(false)}
                onTextExtracted={handleSmartCaptureSubmit}
            />
        </div>
    );
}
